// src/components/DailyReport.tsx
import React, { useState, useEffect, useRef } from 'react';
import { retrieveDailyReport, getExportData } from '../utils/api';
import { useSettings } from '../context/SettingsContext'; // Assuming you have a context for settings
import { useNavigate } from 'react-router-dom';
import { format } from "date-fns";
import { displayPrice } from '../utils/general';


interface HomeProps {
    setDisableOverflow: React.Dispatch<React.SetStateAction<boolean>>;
}

const DailyReport: React.FC<HomeProps> = ({ setDisableOverflow }) => {

    useEffect(() => {
        // Disable overflow for this page
        setDisableOverflow(true);

        // Optionally, you can reset overflow state when leaving the page
        return () => setDisableOverflow(false); // Optional cleanup when leaving page
    }, [setDisableOverflow]);

    const { settings } = useSettings(); // Use settings to retrieve vendorId
    const navigate = useNavigate();

    const formatDateTimeLocal = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`; // Local time in 'YYYY-MM-DDTHH:MM' format
    };

    useEffect(() => {
        const yesterdayMidnight = new Date();
        yesterdayMidnight.setDate(yesterdayMidnight.getDate() - 1);
        yesterdayMidnight.setHours(0, 0, 0, 0); // Set to 00:00 local time

        const todayEndTime = new Date();
        todayEndTime.setHours(23, 59, 0, 0); // Set to 23:59 local time

        setStartDatetime(formatDateTimeLocal(yesterdayMidnight));
        setEndDatetime(formatDateTimeLocal(todayEndTime));
    }, []);

    const [startDatetime, setStartDatetime] = useState('');
    const [endDatetime, setEndDatetime] = useState('');
    const [reportData, setReportData] = useState<any>(null);
    // const [exportData, setExportData] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const handleRetrieveDailyReport = async () => {

        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const vendorId = userData.vendor_id; // Get vendor ID from user data
        const token = localStorage.getItem('token'); // Retrieve token from localStorage or context

        if (!vendorId || !token) {
            alert("Vendor ID or token is missing");
            return;
        }

        setLoading(true);
        try {
            const data = await retrieveDailyReport(vendorId, token, startDatetime, endDatetime);
            setReportData(data);
        } catch (error) {
            console.error("Failed to retrieve daily report", error);
            alert("Error retrieving daily report");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        // Trigger data retrieval whenever startDatetime or endDatetime changes
        if (startDatetime && endDatetime) {
            handleRetrieveDailyReport();
        }
    }, [startDatetime, endDatetime]); // Dependency array ensures this runs when these states change

    const handleRetrieveDailyReportToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);

        // Update state only; useEffect will handle the data retrieval
        setStartDatetime(formatDateTimeLocal(today));
        setEndDatetime(formatDateTimeLocal(endOfToday));
    };

    const handleRetrieveDailyReportYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        const endOfYesterday = new Date(yesterday);
        endOfYesterday.setHours(23, 59, 59, 999);

        // Update state only; useEffect will handle the data retrieval
        setStartDatetime(formatDateTimeLocal(yesterday));
        setEndDatetime(formatDateTimeLocal(endOfYesterday));
    };

    const handleRetrieveDailyReportLastWeek = () => {
        const today = new Date();
        const lastWeekStart = new Date();
        lastWeekStart.setDate(today.getDate() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date();
        lastWeekEnd.setDate(today.getDate() - 1);
        lastWeekEnd.setHours(23, 59, 59, 999);

        // Update state only; useEffect will handle the data retrieval
        setStartDatetime(formatDateTimeLocal(lastWeekStart));
        setEndDatetime(formatDateTimeLocal(lastWeekEnd));
    };

    // Handle report for this week
    const handleRetrieveDailyReportThisWeek = () => {
        const today = new Date();
        const startOfWeek = new Date();
        startOfWeek.setDate(today.getDate() - today.getDay()); // Set to the start of this week (Sunday)
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date();
        endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // Set to the end of this week (Saturday)
        endOfWeek.setHours(23, 59, 59, 999);

        setStartDatetime(formatDateTimeLocal(startOfWeek));
        setEndDatetime(formatDateTimeLocal(endOfWeek));
        handleRetrieveDailyReport();
    };

    // Handle report for this month
    const handleRetrieveDailyReportThisMonth = () => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1); // Start of this month
        startOfMonth.setHours(0, 0, 0, 0);

        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of this month
        endOfMonth.setHours(23, 59, 59, 999);

        setStartDatetime(formatDateTimeLocal(startOfMonth));
        setEndDatetime(formatDateTimeLocal(endOfMonth));
        handleRetrieveDailyReport();
    };

    // Handle report for last month
    const handleRetrieveDailyReportLastMonth = () => {
        const today = new Date();
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1); // Start of last month
        startOfLastMonth.setHours(0, 0, 0, 0);

        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of last month
        endOfLastMonth.setHours(23, 59, 59, 999);

        setStartDatetime(formatDateTimeLocal(startOfLastMonth));
        setEndDatetime(formatDateTimeLocal(endOfLastMonth));
        handleRetrieveDailyReport();
    };


    const formatDateHumanReadable = (datetime: string) => {
        const date = new Date(datetime);
        return format(date, "dd-MM-yy (HH:mm)"); // Using date-fns for formatting
    };

    return (
        <div className='flex flex-col '>
            <div className='flex flex-row justify-between '>
                <h2 className='pageTitle'>Daily Report</h2>
                <button onClick={() => navigate('/export')} disabled={loading} className='ActionBtn bg-blue-500'>
                    {loading ? "Loading..." : "Export Data"}
                </button>
            </div>
            <div className='flex flex-col md:flex-row items-center justify-center w-screen p-2'>
                <div>
                    <label>Start Datetime:</label>
                    <input
                        type="datetime-local"
                        value={startDatetime}
                        onChange={(e) => setStartDatetime(e.target.value)}
                        className='border p-2'
                    />
                </div>
                <div>
                    <label>End Datetime:</label>
                    <input
                        type="datetime-local"
                        value={endDatetime}
                        onChange={(e) => setEndDatetime(e.target.value)}
                        className='border p-2'

                    />
                </div>
            </div>

            <div className='flex flex-row items-center justify-center w-screen p-2  shadow-xl'>
                <div>
                    <button onClick={handleRetrieveDailyReportToday} disabled={loading} className='ActionBtn bg-green-500'>
                        {loading ? "Loading..." : "Today"}
                    </button>
                    <button onClick={handleRetrieveDailyReportYesterday} disabled={loading} className='ActionBtn bg-green-500'>
                        {loading ? "Loading..." : "Yesterday"}
                    </button>
                    <button onClick={handleRetrieveDailyReportThisWeek} disabled={loading} className='ActionBtn bg-green-500'>
                        {loading ? "Loading..." : "This Week"}
                    </button>
                    <button onClick={handleRetrieveDailyReportLastWeek} disabled={loading} className='ActionBtn bg-green-500'>
                        {loading ? "Loading..." : "Last Week"}
                    </button>
                    <button onClick={handleRetrieveDailyReportThisMonth} disabled={loading} className='ActionBtn bg-green-500'>
                        {loading ? "Loading..." : "This Month"}
                    </button>
                    <button onClick={handleRetrieveDailyReportLastMonth} disabled={loading} className='ActionBtn bg-green-500'>
                        {loading ? "Loading..." : "Last Month"}
                    </button>
                </div>
                <button onClick={handleRetrieveDailyReport} disabled={loading} className='ActionBtn bg-green-500'>
                    {loading ? "Loading..." : "Daily Report"}
                </button>

            </div>

            <div className='flex flex-col items-start justify-start w-screen h-screen  p-2 '>
                {/* <div className='w-1/2'>
                    {reportData && (
                        <div>
                            <h3>Daily Report Data:</h3>
                            <pre>{JSON.stringify(reportData, null, 2)}</pre>
                        </div>
                    )}
                </div> */}


                <div className="w-full p-4">
                    {reportData && (
                        <div className="space-y-8">
                            {/* Report Summary */}
                            <div className="bg-white shadow-md rounded-lg p-4">
                                <h3 className="text-lg font-semibold mb-4">Daily Report Summary - {formatDateHumanReadable(startDatetime)} - {formatDateHumanReadable(endDatetime)}</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><strong>Order Income:</strong> RM {reportData.orderIncome}</div>
                                    <div><strong>Order Count:</strong> {reportData.orderCount}</div>
                                    <div><strong>Total Order Amount:</strong> RM {reportData.totalOrderAmount}</div>
                                    <div><strong>Finished Order Count:</strong> {reportData.finishedOrderCount}</div>
                                    <div><strong>Average Price:</strong> RM {parseFloat(reportData.averagePrice).toFixed(2)}</div>
                                    <div><strong>Refund Amount:</strong> RM {reportData.refundAmount}</div>
                                    <div><strong>Refund Count:</strong> {reportData.refundCount}</div>
                                    <div><strong>Total Quantity:</strong> {reportData.totalQuantity}</div>
                                </div>
                            </div>

                            {/* Dishes Ranking */}
                            <div className="bg-white shadow-md rounded-lg p-4">
                                <h3 className="text-lg font-semibold mb-4">Dishes Ranking</h3>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    {reportData.dishesRanking.map((dish: any, index: number) => (
                                        <div key={index} className="flex items-center space-x-4 bg-gray-100 p-3 rounded-lg">
                                            <img
                                                src={dish.imagePath}
                                                alt={dish.name}
                                                crossOrigin="anonymous"

                                                className="w-16 h-16 object-cover rounded-full"
                                            />
                                            <div>
                                                <h4 className="font-bold">{dish.name}</h4>
                                                <p><strong>Total:</strong> {dish.total}</p>
                                                <p><strong>Paid Quantity:</strong> {dish.paidQuantity}</p>
                                                <p><strong>Refund Quantity:</strong> {dish.refundQuantity}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Payment Methods */}
                            <div className="bg-white shadow-md rounded-lg p-4 pb-28">
                                <h3 className="text-lg font-semibold mb-4">Payment Methods</h3>
                                <table className="w-full table-auto border-collapse border border-gray-300">
                                    <thead>
                                        <tr className="bg-gray-200">
                                            <th className="border border-gray-300 p-2 text-left">Method</th>
                                            <th className="border border-gray-300 p-2 text-right">Total Amount (RM)</th>
                                            <th className="border border-gray-300 p-2 text-right">Count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.paymentMethods.map((method: any, index: number) => (
                                            <tr key={index} className="bg-white hover:bg-gray-100">
                                                <td className="border border-gray-300 p-2">{method.name}</td>
                                                <td className="border border-gray-300 p-2 text-right">{Number(method.totalAmount).toFixed(2)}</td>
                                                <td className="border border-gray-300 p-2 text-right">{method.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-100 font-semibold">
                                            <td className="border border-gray-300 p-2 text-left">Total</td>
                                            <td className="border border-gray-300 p-2 text-right">
                                                {reportData.paymentMethods
                                                    .reduce((sum: number, method: any) => sum + Number(method.totalAmount), 0)
                                                    .toFixed(2)}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-right">
                                                {reportData.paymentMethods.reduce((sum: number, method: any) => sum + method.count, 0)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>


                        </div>
                    )}
                </div>

            </div >
        </div>

    );
};

export default DailyReport;
